import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import { RadioGroup } from '@atlaskit/radio';
import type { OptionsPropType } from '@atlaskit/radio/dist/types/types';
import Spinner from '@atlaskit/spinner';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  ApiRequestError,
  demandPartnerServices,
  fetchActiveApps,
  type DpConfigDetails
} from '@shared/api';
import { countryOptions } from '@shared/lib/constant';
import { useAppSelector } from '@shared/store/hooks';
import MultiSelectInput, { type MultiSelectOption } from '@shared/ui/MultiSelectInput';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const cardStyles = {
  background: token('color.background.input'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: '18px',
  padding: token('space.300')
} as const;

const dividerStyles = {
  background: token('color.border'),
  height: '1px',
  width: '100%'
} as const;

const formatOptions = [
  { label: 'Banner', value: 'banner' },
  { label: 'Video', value: 'video' },
  { label: 'Native', value: 'native' }
] satisfies MultiSelectOption<'banner' | 'video' | 'native'>[];

const platformOptions = [
  { label: 'Android', value: 'android' },
  { label: 'iOS', value: 'ios' },
  { label: 'Web', value: 'web' }
] satisfies MultiSelectOption<'android' | 'ios' | 'web'>[];

const multiFormatSupportOptions = [
  { label: 'Enabled', value: 'true' },
  { label: 'Disabled', value: 'false' }
] satisfies OptionsPropType;

const isMissingConfigError = (error: unknown) =>
  error instanceof ApiRequestError && error.status === 400;

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Unable to load demand partner config.';

const buildConfigDraft = (config: DpConfigDetails | undefined): DpConfigDetails => ({
  dpId: config?.dpId,
  allowedCountryList: config?.allowedCountryList ?? {},
  allowedAppsList: config?.allowedAppsList ?? {},
  allowedFormats: (config?.allowedFormats ?? {}) as DpConfigDetails['allowedFormats'],
  allowedPlatforms: (config?.allowedPlatforms ?? {}) as DpConfigDetails['allowedPlatforms'],
  multiFormatSupport: config?.multiFormatSupport ?? false
});

const toRecord = <T extends string>(options: MultiSelectOption<T>[]) =>
  Object.fromEntries(options.map((option) => [option.value, null])) as Record<T, null>;

const mergeOptions = <T extends string>(
  baseOptions: MultiSelectOption<T>[],
  selectedKeys: string[]
): MultiSelectOption<T>[] => {
  const seen = new Set(baseOptions.map((option) => option.value));
  const extraOptions = selectedKeys
    .filter((key): key is T => !seen.has(key as T))
    .map((key) => ({ label: key, value: key }));

  return [...baseOptions, ...extraOptions];
};

const mapRecordToSelectedOptions = <T extends string>(
  record: Record<string, null> | undefined,
  options: MultiSelectOption<T>[]
) => {
  const optionMap = new Map(options.map((option) => [option.value, option]));

  return Object.keys(record ?? {}).map((key) => optionMap.get(key as T) ?? { label: key, value: key as T });
};

export default function DemandPartnerConfigPage() {
  const { dpId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const demandPartnerProfile = useAppSelector((state) =>
    dpId ? state.partner.demandPartnerProfiles[dpId] : undefined
  );
  const inventoryActiveApps = useAppSelector((state) => state.inventory.activeApps);

  const [draft, setDraft] = useState<DpConfigDetails | undefined>(undefined);

  const breadcrumbs = (
    <Breadcrumbs>
      <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
      <BreadcrumbsItem
        text="Demand Partners"
        key="Demand Partners"
        onClick={() => navigate('/demand-partners')}
      />
      <BreadcrumbsItem
        text={dpId}
        key={dpId}
        onClick={() => navigate(`/partner/dp/${dpId}`)}
      />
      <BreadcrumbsItem text="Config" key="Config" />
    </Breadcrumbs>
  );

  const activeAppsQuery = useQuery({
    queryKey: ['activeAppsForDpConfig'],
    queryFn: fetchActiveApps,
    enabled: inventoryActiveApps.length === 0
  });

  const configQuery = useQuery({
    queryKey: ['demandPartnerConfigDetails', dpId],
    queryFn: (): Promise<DpConfigDetails> => demandPartnerServices.getDpConfigDetails(dpId),
    enabled: Boolean(dpId),
    retry: false
  });

  useEffect(() => {
    if (configQuery.isLoading) {
      return;
    }

    if (configQuery.error && !isMissingConfigError(configQuery.error)) {
      return;
    }

    setDraft(buildConfigDraft(configQuery.data));
  }, [configQuery.data, configQuery.error, configQuery.isLoading]);

  const activeApps = activeAppsQuery.data ?? inventoryActiveApps;

  const appOptions = useMemo<MultiSelectOption[]>(
    () =>
      mergeOptions(
        activeApps.map((app) => ({
          label: app.appName?.trim() ? `${app.appName}` : app.appId,
          value: app.appId
        })),
        Object.keys(draft?.allowedAppsList ?? {})
      ),
    [activeApps, draft?.allowedAppsList]
  );

  const countrySelectOptions = useMemo<MultiSelectOption[]>(
    () =>
      mergeOptions(
        countryOptions.map(({ code, label }) => ({
          label: `${label} (${code})`,
          value: code
        })),
        Object.keys(draft?.allowedCountryList ?? {})
      ),
    [draft?.allowedCountryList]
  );

  const resolvedFormatOptions = useMemo(
    () => mergeOptions(formatOptions, Object.keys(draft?.allowedFormats ?? {})),
    [draft?.allowedFormats]
  );

  const resolvedPlatformOptions = useMemo(
    () => mergeOptions(platformOptions, Object.keys(draft?.allowedPlatforms ?? {})),
    [draft?.allowedPlatforms]
  );

  const selectedCountries = useMemo(
    () => mapRecordToSelectedOptions(draft?.allowedCountryList, countrySelectOptions),
    [countrySelectOptions, draft?.allowedCountryList]
  );

  const selectedApps = useMemo(
    () => mapRecordToSelectedOptions(draft?.allowedAppsList, appOptions),
    [appOptions, draft?.allowedAppsList]
  );

  const selectedFormats = useMemo(
    () => mapRecordToSelectedOptions(draft?.allowedFormats, resolvedFormatOptions),
    [draft?.allowedFormats, resolvedFormatOptions]
  );

  const selectedPlatforms = useMemo(
    () => mapRecordToSelectedOptions(draft?.allowedPlatforms, resolvedPlatformOptions),
    [draft?.allowedPlatforms, resolvedPlatformOptions]
  );

  const savePayload = useMemo<DpConfigDetails | undefined>(() => {
    if (!draft) {
      return undefined;
    }

    return {
      dpId: draft.dpId,
      allowedCountryList: draft.allowedCountryList,
      allowedAppsList: draft.allowedAppsList,
      allowedFormats: draft.allowedFormats,
      allowedPlatforms: draft.allowedPlatforms,
      multiFormatSupport: draft.multiFormatSupport
    };
  }, [draft]);

  const updateConfigMutation = useMutation({
    mutationFn: (payload: DpConfigDetails) => demandPartnerServices.updateDpConfigDetails(dpId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['demandPartnerConfigDetails', dpId] });
    }
  });

  const isLoading = configQuery.isLoading || (inventoryActiveApps.length === 0 && activeAppsQuery.isLoading);

  if (!dpId) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Demand Partner Config</PageHeader>
        <Text>Demand partner ID not found.</Text>
      </Stack>
    );
  }

  if (isLoading || !draft) {
    return (
      <Box
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '70vh',
          justifyContent: 'center'
        }}
      >
        <Spinner size="large" />
      </Box>
    );
  }

  if (activeAppsQuery.error) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Demand Partner Config</PageHeader>
        <Text color="color.text.danger">{getErrorMessage(activeAppsQuery.error)}</Text>
      </Stack>
    );
  }

  if (configQuery.error && !isMissingConfigError(configQuery.error)) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Demand Partner Config</PageHeader>
        <Text color="color.text.danger">{getErrorMessage(configQuery.error)}</Text>
      </Stack>
    );
  }

  return (
    <Stack space="space.300" xcss={{ width: '100%' }}>
      <PageHeader
        breadcrumbs={breadcrumbs}
        actions={
          <Inline space="space.100">
            <Button appearance="subtle" onClick={() => navigate(`/partner/dp/${dpId}`)}>
              Back to details
            </Button>
            <Button
              appearance="primary"
              isDisabled={!savePayload}
              isLoading={updateConfigMutation.isPending}
              onClick={() => {
                if (savePayload) {
                  updateConfigMutation.mutate(savePayload);
                }
              }}
            >
              Save config
            </Button>
          </Inline>
        }
      >
        {demandPartnerProfile?.dpName ?? `Demand Partner ${dpId}`} Config
      </PageHeader>

      <Box
        style={{
          alignItems: 'center',
          background: token('color.background.neutral.subtle'),
          borderRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          padding: token('space.250')
        }}
      >
        <div>
          <Text size="large" weight="bold">
            {dpId}
          </Text>
          <Text color="color.text.subtle">
            Manage allowed countries, apps, formats, platforms, and multi-format support for this demand partner.
          </Text>
        </div>
      </Box>

      {updateConfigMutation.isSuccess ? (
        <Text color="color.text.success">Config updated successfully.</Text>
      ) : null}
      {updateConfigMutation.error ? (
        <Text color="color.text.danger">{getErrorMessage(updateConfigMutation.error)}</Text>
      ) : null}

      <Box style={cardStyles}>
        <Stack space="space.250">
          <Text size="large" weight="medium">
            Country and App Access
          </Text>
          <Box style={dividerStyles} />

          <MultiSelectInput
            inputId="allowed-country-list"
            label="Allowed country list"
            options={countrySelectOptions}
            value={selectedCountries}
            onChange={(value) => {
              setDraft((currentDraft) =>
                currentDraft
                  ? {
                      ...currentDraft,
                      allowedCountryList: toRecord(value)
                    }
                  : currentDraft
              );
            }}
            placeholder="Select allowed countries"
            // helpText="Countries will be saved as keys in allowedCountryList."
            enableSelectAll
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />

          <MultiSelectInput
            inputId="allowed-apps-list"
            label="Allowed apps list"
            options={appOptions}
            value={selectedApps}
            onChange={(value) => {
              setDraft((currentDraft) =>
                currentDraft
                  ? {
                      ...currentDraft,
                      allowedAppsList: toRecord(value)
                    }
                  : currentDraft
              );
            }}
            placeholder="Select allowed apps"
            // helpText="Apps will be saved as keys in allowedAppsList."
            enableSelectAll
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </Stack>
      </Box>

      <Box style={cardStyles}>
        <Stack space="space.250">
          <Text size="large" weight="medium">
            Format and Platform Rules
          </Text>
          <Text color="color.text.subtle">
            Choose which ad formats and platforms are allowed for this demand partner.
          </Text>
          <Box style={dividerStyles} />

          <MultiSelectInput
            inputId="allowed-formats"
            label="Allowed formats"
            options={resolvedFormatOptions}
            value={selectedFormats}
            onChange={(value) => {
              setDraft((currentDraft) =>
                currentDraft
                  ? {
                      ...currentDraft,
                      allowedFormats: toRecord(value) as DpConfigDetails['allowedFormats']
                    }
                  : currentDraft
              );
            }}
            placeholder="Select allowed formats"
            // helpText="Formats will be saved as keys in allowedFormats."
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />

          <MultiSelectInput
            inputId="allowed-platforms"
            label="Allowed platforms"
            options={resolvedPlatformOptions}
            value={selectedPlatforms}
            onChange={(value) => {
              setDraft((currentDraft) =>
                currentDraft
                  ? {
                      ...currentDraft,
                      allowedPlatforms: toRecord(value) as DpConfigDetails['allowedPlatforms']
                    }
                  : currentDraft
              );
            }}
            placeholder="Select allowed platforms"
            // helpText="Platforms will be saved as keys in allowedPlatforms."
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </Stack>
      </Box>

      <Box style={cardStyles}>
        <Stack space="space.250">
          <Text size="large" weight="medium">
            Multi-Format Support
          </Text>
          <Text
            id="multi-format-support-label"
            color="color.text.subtle"
            size="small"
          >
            Set whether this demand partner supports multiple formats at the same time.
          </Text>
          <Box style={dividerStyles} />
          <RadioGroup
            aria-labelledby="multi-format-support-label"
            name="multi-format-support"
            options={multiFormatSupportOptions}
            value={String(draft.multiFormatSupport)}
            onChange={(event) => {
              const nextValue = event.currentTarget.value === 'true';
              setDraft((currentDraft) =>
                currentDraft
                  ? {
                      ...currentDraft,
                      multiFormatSupport: nextValue
                    }
                  : currentDraft
              );
            }}
          />
        </Stack>
      </Box>
    </Stack>
  );
}
